import { supabase } from '../lib/supabase';
import {
  SubmitGroupBookingRequest,
  SubmitGroupBookingResponse,
  GetGroupAvailableDatesResponse,
  AdminUpdateGroupBookingRequest,
  AdminUpdateGroupBookingResponse,
  AdminManageGroupDatesRequest,
  AdminManageGroupDatesResponse,
  GroupBooking,
  LookupGroupBookingResponse,
} from '../types/groupBooking';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

/**
 * 월별 예약 가능 날짜 조회 (공개)
 */
export const getGroupAvailableDates = async (
  programId: string,
  year: number,
  month: number
): Promise<GetGroupAvailableDatesResponse> => {
  try {
    const response = await fetch(
      `${supabaseUrl}/functions/v1/get-group-available-dates?program_id=${programId}&year=${year}&month=${month}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
        },
      }
    );

    return await response.json();
  } catch (error) {
    console.error('Error fetching group available dates:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: '네트워크 오류가 발생했습니다.',
      },
    };
  }
};

/**
 * 단체체험 예약 신청 (공개, 비회원 허용)
 */
export const submitGroupBooking = async (
  request: SubmitGroupBookingRequest
): Promise<SubmitGroupBookingResponse> => {
  try {
    // 로그인 상태면 토큰 전달 (선택)
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'apikey': supabaseAnonKey,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/submit-group-booking`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    return await response.json();
  } catch (error) {
    console.error('Error submitting group booking:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: '네트워크 오류가 발생했습니다.',
      },
    };
  }
};

/**
 * 관리자: 단체체험 예약 상태 변경
 */
export const adminUpdateGroupBooking = async (
  request: AdminUpdateGroupBookingRequest
): Promise<AdminUpdateGroupBookingResponse> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return {
        success: false,
        error: { code: 'UNAUTHORIZED', message: '로그인이 필요합니다.' },
      };
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/admin-update-group-booking`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify(request),
    });

    return await response.json();
  } catch (error) {
    console.error('Error updating group booking:', error);
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: '네트워크 오류가 발생했습니다.' },
    };
  }
};

/**
 * 관리자: 예약 가능 날짜 관리
 */
export const adminManageGroupDates = async (
  request: AdminManageGroupDatesRequest
): Promise<AdminManageGroupDatesResponse> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return {
        success: false,
        error: { code: 'UNAUTHORIZED', message: '로그인이 필요합니다.' },
      };
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/admin-manage-group-dates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify(request),
    });

    return await response.json();
  } catch (error) {
    console.error('Error managing group dates:', error);
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: '네트워크 오류가 발생했습니다.' },
    };
  }
};

/**
 * 비회원 예약 조회 (예약번호 + 전화번호)
 */
export const lookupGroupBooking = async (
  bookingNumber: string,
  phone: string
): Promise<LookupGroupBookingResponse> => {
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/lookup-group-booking`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ booking_number: bookingNumber, phone }),
    });

    return await response.json();
  } catch (error) {
    console.error('Error looking up group booking:', error);
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: '네트워크 오류가 발생했습니다.' },
    };
  }
};

/**
 * 관리자: 단체체험 예약 목록 조회 (직접 Supabase 쿼리)
 */
export const getGroupBookings = async (
  filters?: { status?: string; search?: string }
): Promise<GroupBooking[]> => {
  try {
    let query = supabase
      .from('group_bookings')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters?.search) {
      query = query.or(
        `institution_name.ilike.%${filters.search}%,booker_name.ilike.%${filters.search}%,booking_number.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching group bookings:', error);
    return [];
  }
};

/**
 * 관리자: 관리자 메모 저장
 */
export const saveGroupBookingMemo = async (
  bookingId: string,
  memo: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('group_bookings')
      .update({ admin_memo: memo, updated_at: new Date().toISOString() })
      .eq('id', bookingId);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error saving memo:', error);
    return false;
  }
};
